import { Box, Tooltip } from '@mui/material';
import { formatTranslation, unsafeTranslationKey } from 'intl';
import { IssueParams } from 'components/common/forms/EditableField/types';
import { useSites } from 'store/site';
import { useUsers } from 'components/dataProviders/withUsers';
import { UserCard } from 'components/user/card';
import { HoverWrapper } from 'components/user/card/hoverWrapper';
import { ReactElement } from 'react';
import { useIntl } from 'react-intl';
import { useProcesses } from 'store/process';
import { logError } from 'setup/logger/logError';
import { EditableStandardEntityName } from 'shared/domain/fieldValue/fields';
import { SiteModel } from 'shared/domain/site/types/model';
import { ProcessType } from 'shared/domain/process/types/model';
import { getAccessLevelIcon } from 'views/issue/issueView/AccessManagement/getIcon';
import { AvailableOption } from './types';

const UserSelectedValue = ({
  option,
  issueParams,
  processes,
  sites,
}: {
  option: AvailableOption;
  issueParams: IssueParams;
  processes: ProcessType[];
  sites: SiteModel[];
}): ReactElement | null => {
  const { getUserOnViewWithAccessLevelOnIconViewById } = useUsers();
  const intl = useIntl();
  const user = getUserOnViewWithAccessLevelOnIconViewById(
    option._id,
    issueParams.userAccesses,
    sites,
    processes
  );
  if (!user) {
    logError('User not found', option._id);
    return null;
  }

  const onHoverComponent = (
    <UserCard
      user={user}
      userAccesses={issueParams?.userAccesses}
      issueId={issueParams?.issueId}
      siteId={issueParams?.siteId}
      processId={issueParams?.processId}
    />
  );
  return (
    <HoverWrapper onHoverComponent={onHoverComponent}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {user.accessLevel && (
          <Tooltip
            title={formatTranslation(intl, unsafeTranslationKey(user.accessLevel.explanationLabel),)}
          >
            {getAccessLevelIcon(user.accessLevel.value, false)}
          </Tooltip>
        )}
        <span>{option?.label}</span>
      </Box>
    </HoverWrapper>
  );
};

const UserSelectedValueWrapper = ({
  option,
  issueParams,
}: {
  option: AvailableOption;
  issueParams: IssueParams;
}): ReactElement => {
  const { processes } = useProcesses();
  const {
    items: sites,
  } = useSites();
  return (
    <UserSelectedValue
      option={option}
      issueParams={issueParams}
      processes={processes}
      sites={sites}
    />
  );
};

export const SelectedValue = ({
  option,
  entityName,
  issueParams,
}: {
  option: AvailableOption | undefined;
  entityName: string | undefined;
  issueParams: IssueParams | null;
}): ReactElement => {
  if (
    entityName === EditableStandardEntityName.user &&
    issueParams &&
    option
  ) {
    return (
      <UserSelectedValueWrapper
        option={option}
        issueParams={issueParams}
      />
    );
  }
  return <span>{option?.label}</span>;
};
